<template>
  <div id="app">
    <CookieBanner ref="cookieBanner" :show-banner="showCookieBanner" @cookies-updated="onCookiesUpdated" />
    <AppNavbar />
    <transition name="fade" mode="out-in">
      <router-view :key="$route.path" />
    </transition>
    <div class="cookie-settings-button" @click="showCookieSettings" v-if="!showCookieBanner">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 2a10 10 0 1 0 10 10 10 10 0 0 0-10-10zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"/>
        <path d="M12 6v2"/>
        <path d="M12 16v2"/>
        <path d="M16 12h2"/>
        <path d="M6 12h2"/>
      </svg>
      <span>Cookie-Einstellungen</span>
    </div>
  </div>
</template>

<script>
import AppNavbar from './components/Navbar.vue';
import CookieBanner from './components/CookieBanner.vue';
import VueCookies from 'vue-cookies';

export default {
  name: 'App',
  components: {
    AppNavbar,
    CookieBanner,
  },
  data() {
    return {
      showCookieBanner: true
    };
  },
  mounted() {
    // Check if cookie settings already exist
    const cookieSettings = VueCookies.get('cookie-settings');
    if (cookieSettings) {
      this.showCookieBanner = false;
    }
    
    // Listen for navigation events
    this.$router.beforeEach((to, from, next) => {
      // Close any mobile menu that might be open
      const navbarToggler = document.querySelector('.navbar-toggler');
      const navbarCollapse = document.querySelector('.navbar-collapse');
      if (navbarToggler && navbarCollapse && navbarCollapse.classList.contains('show')) {
        navbarToggler.click();
      }
      
      // For SPA, handle scrolling to top only on full route changes, not hash changes
      if (!to.hash && to.path !== from.path) {
        window.scrollTo(0, 0);
      }
      
      next();
    });
  },
  methods: {
    showCookieSettings() {
      this.showCookieBanner = true;
    },
    onCookiesUpdated() {
      this.showCookieBanner = false;
    }
  }
};
</script>

<style lang="scss">
@import "@/assets/css/global.scss"; // Importiere die globale SCSS-Datei

// Grundlegende Styles
body {
  background: linear-gradient(145deg, lighten($background-color, 3%), darken($background-color, 5%));
  color: $text-color;
  font-family: 'Arial', sans-serif;
  margin: 0;
  padding: 0;
  font-size: 16px;
  line-height: 1.7;
  overflow-x: hidden;
}

// Layout Containers
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

// Sektionen
section {
  padding: 100px 0;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
}

// Typografie
h1, h2, h3, h4, h5 {
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.3;
}

h1 {
  font-size: 3.5rem;
  font-weight: 800;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
}

h2 {
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
}

h3 {
  font-size: 1.75rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
}

p {
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
}

// Utility Klassen
.text-gradient {
  background: $accent-gradient;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.section-title {
  margin-bottom: 3rem;
  
  .subtitle {
    display: inline-block;
    font-size: 0.9rem;
    font-weight: 600;
    color: $accent-color;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 1rem;
    position: relative;
    padding-left: 30px;
    
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      width: 20px;
      height: 2px;
      background: $accent-gradient;
      transform: translateY(-50%);
    }
  }
  
  h2 {
    margin-bottom: 1rem;
  }
}

// Animationen
.animated {
  animation-duration: 1s;
  animation-fill-mode: both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fadeInUp {
  animation-name: fadeInUp;
}

.fadeInDown {
  animation-name: fadeInDown;
}

// Cookie Settings Button
.cookie-settings-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: rgba($primary-color, 0.9);
  border: 1px solid rgba($accent-color, 0.3);
  color: $text-color;
  padding: 8px 15px;
  border-radius: 30px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  z-index: 900;
  backdrop-filter: blur(5px);
  
  &:hover {
    background: rgba($accent-color, 0.1);
    transform: translateY(-3px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
  
  svg {
    color: $accent-color;
  }
  
  span {
    font-weight: 600;
  }
}

// Hero Section Styles
.hero-shape-1, .hero-shape-2 {
  position: absolute;
  z-index: -1;
  opacity: 0.6;
}

.hero-shape-1 {
  top: 20%;
  right: 10%;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba($accent-color, 0.2) 0%, rgba($accent-color, 0) 70%);
  animation: float 6s ease-in-out infinite;
}

.hero-shape-2 {
  bottom: 10%;
  left: 5%;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba($accent-color, 0.1) 0%, rgba($accent-color, 0) 70%);
  animation: float 8s ease-in-out infinite;
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
  100% {
    transform: translateY(0px);
  }
}

// CTA Buttons
.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 2rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
}

// Page Transitions
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter {
  opacity: 0;
  transform: translateY(10px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.btn-secondary {
  background: transparent;
  color: $accent-color;
  border: 2px solid $accent-color;
  padding: 12px 28px;
  border-radius: 8px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba($accent-color, 0.1);
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba($accent-color, 0.2);
  }
}

// Features Grid
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-top: 3rem;
}

.feature-item {
  background: rgba($secondary-color, 0.5);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid rgba($accent-color, 0.1);
  backdrop-filter: blur(10px);
  
  &:hover {
    transform: translateY(-10px);
    border-color: rgba($accent-color, 0.3);
  }
  
  .feature-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: linear-gradient(145deg, rgba($accent-color, 0.1), rgba($accent-color, 0.05));
    font-size: 2rem;
    color: $accent-color;
  }
  
  h3 {
    font-size: 1.3rem;
    margin-bottom: 1rem;
    color: $heading-color;
  }
  
  p {
    color: $muted-text-color;
    font-size: 0.95rem;
    margin-bottom: 0;
  }
}
</style>

